/**
 * Phase 13.1 — admin-only fallback panel that surfaces forgot-password
 * reset links **only when the email backend is mocked**. The backend
 * `/api/admin/password-reset-links` endpoint returns `visible:false` once
 * a real SMTP / SendGrid / Resend provider is wired up, in which case
 * this panel renders nothing.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, MailWarning, RefreshCw, Clock as ClockIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "../../lib/api";

function timeRemaining(expiresIso) {
  if (!expiresIso) return "—";
  const ms = new Date(expiresIso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
}

export default function PendingResetLinksPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copyId, setCopyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get(
        "/admin/password-reset-links?status=pending",
      );
      setData(resp);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Real email is wired up — hide the panel completely.
  if (!loading && data && data.visible === false) return null;
  // Permission denied (non-admin) — hide silently.
  if (error && /forbidden|admin/i.test(error)) return null;

  const items = data?.items || [];

  const copy = async (item) => {
    if (!item.reset_link) return;
    try {
      await navigator.clipboard.writeText(item.reset_link);
      setCopyId(item.id);
      toast.success(`Reset link for ${item.user_email} copied`);
      window.setTimeout(() => setCopyId(null), 2500);
    } catch {
      toast.error("Couldn't copy — please select the link manually.");
    }
  };

  return (
    <section
      data-testid="pending-reset-links-panel"
      className="brand-card p-5"
      aria-labelledby="pending-reset-links-heading"
    >
      <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{
              backgroundColor: "var(--warning-bg)",
              color: "var(--warning-fg)",
              border: "1px solid var(--warning-border)",
            }}
          >
            <MailWarning className="h-4 w-4" strokeWidth={1.7} />
          </div>
          <div>
            <h2
              id="pending-reset-links-heading"
              className="text-sm font-semibold"
              style={{ color: "var(--page-fg)" }}
            >
              Pending reset links
              <span
                className="ml-2 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: "var(--warning-bg)",
                  color: "var(--warning-fg)",
                  boxShadow: "inset 0 0 0 1px var(--warning-border)",
                }}
              >
                Email mocked
              </span>
            </h2>
            <p
              className="mt-0.5 text-xs"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              Until production email is wired up, copy the link below and hand
              it off securely to the requesting user. This panel disappears
              automatically once a real email provider is configured.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          data-testid="pending-reset-links-refresh"
        >
          <RefreshCw
            className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </header>

      {loading ? (
        <div
          className="h-20 animate-pulse rounded-md"
          style={{ backgroundColor: "var(--surface-2)" }}
        />
      ) : items.length === 0 ? (
        <p
          className="rounded-md border border-dashed px-3 py-4 text-center text-xs"
          data-testid="pending-reset-links-empty"
          style={{
            borderColor: "var(--border-subtle)",
            color: "var(--page-fg-muted)",
            backgroundColor: "var(--surface-2)",
          }}
        >
          No pending reset links.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--border-subtle)]">
          {items.map((item) => (
            <li
              key={item.id}
              data-testid={`pending-reset-link-row-${item.id}`}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p
                  className="truncate text-sm font-medium"
                  style={{ color: "var(--page-fg)" }}
                >
                  {item.user_email}
                </p>
                <p
                  className="mt-0.5 inline-flex items-center gap-1.5 text-[11px]"
                  style={{ color: "var(--page-fg-muted)" }}
                >
                  <ClockIcon className="h-3 w-3" strokeWidth={1.8} />
                  Expires in{" "}
                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {timeRemaining(item.expires_at)}
                  </span>
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(item)}
                data-testid={`pending-reset-link-copy-${item.id}`}
                className="shrink-0"
                disabled={!item.reset_link}
              >
                <Copy className="mr-1 h-3.5 w-3.5" />
                {copyId === item.id ? "Copied" : "Copy reset link"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
